import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { showErrorToast } from "#app/notifications";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { getQuoteRequestLineState } from "#modules/quoteRequestLines/domain/getQuoteRequestLineState";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { useStrategy } from "#modules/quoteRequestLines/view/useStrategy";
import { useCheckPart } from "#modules/similarPart/view/useCheckPart";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { AssemblyWithChildren, is3DFile } from "shared";

import { useCurrentAssembly } from "../QuoteRequestLine/useCurrentAssembly";
import { StateRenderer } from "./StateRenderer";

type Props = {
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  predictProcessSteps: (props: {
    quoteRequestLineId: string;
    shouldHandleAssembly: boolean;
    failedOrUnStartedChildrenOnly: boolean;
  }) => void;
  isPredictionError: boolean;
  quoteRequestLine: QuoteRequestLineDto;
  part: NonNullable<PartDto>;
  openDetailsPanelForQuoteRequestLine: (quoteRequestLineId: string) => void;
  onOpenThumbnailOverlay: () => void;
  hasChildren: boolean;
  isChild: boolean;
};

export const QuoteRequestLineRightSection = ({
  assembliesInBatch,
  predictProcessSteps,
  isPredictionError,
  quoteRequestLine,
  openDetailsPanelForQuoteRequestLine,
  part,
  onOpenThumbnailOverlay,
  hasChildren,
  isChild,
}: Props) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const { control } = useFormContext<QuoteRequestLineFormData>();
  const materialGrade = useWatch({ control, name: "materialGrade" });
  const quantity = useWatch({ control, name: "quantity" });

  const { modelType, isClustering } = useStrategy();

  const { checkPart: analyzePart, isPending: isCheckingPart } = useCheckPart({
    onSuccess: () => {
      void queryClient.invalidateQueries(quoteRequestLineQuery({ id: quoteRequestLine.id }));
    },
  });

  const currentAssembly = useCurrentAssembly(quoteRequestLine.id);

  const dataDrivenState = getQuoteRequestLineState({
    quoteRequestLine,
    part,
    materialGrade,
    isChild,
    hasChildren,
    assembliesInBatch,
    currentAssemblyChildren: currentAssembly?.children || [],
    isClustering,
    modelType,
  });

  // These two states can't be derived from the DTO alone: they reflect a mutation that's live
  // right now (an in-flight re-analysis or a predict call that just failed client-side), ahead of
  // the backend persisting that status. Both only take effect if nothing more specific already
  // fired, preserving the original state-machine's priority order.
  const firstFile = part.files[0];
  const isCheckingPartOverride =
    dataDrivenState !== "analyzingPart" &&
    dataDrivenState !== "analyzingAssembly" &&
    isCheckingPart &&
    firstFile &&
    is3DFile(firstFile);

  const currentState = isCheckingPartOverride
    ? "analyzingPart"
    : dataDrivenState === "predicted" && isPredictionError
      ? "predictionError"
      : dataDrivenState;

  useEffect(() => {
    analytics.capture(AnalyticsEvent.QrlPredictionStateShown, {
      state: currentState,
      quote_request_line_id: quoteRequestLine.id,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState]);

  const handleRetryPrediction = () => {
    if (!materialGrade) {
      showErrorToast({
        title: t("qrl.error.materialGradeMissing"),
        message: t("qrl.error.materialGradeMissingMessage"),
      });
      return;
    }
    predictProcessSteps({
      quoteRequestLineId: quoteRequestLine.id,
      shouldHandleAssembly: true,
      failedOrUnStartedChildrenOnly: true,
    });
  };

  const handleRetryAnalysis = () => {
    analyzePart({ id: part.id, quoteRequestLineId: quoteRequestLine.id });
  };

  const onOpenDetailsPanel = () => {
    openDetailsPanelForQuoteRequestLine(quoteRequestLine.id);
  };

  return (
    <StateRenderer
      currentState={currentState}
      quoteRequestLine={quoteRequestLine}
      materialGrade={materialGrade}
      quantity={quantity || 0}
      openDetailsPanel={onOpenDetailsPanel}
      onRetryPrediction={handleRetryPrediction}
      onRetryAnalysis={handleRetryAnalysis}
      onOpenThumbnailOverlay={onOpenThumbnailOverlay}
      hasChildren={hasChildren}
    />
  );
};
